import "./OrderDepthComponent.scss";
import templateMobile from "./OrderDepthMobileComponent.html";
import templateDesktop from "./OrderDepthDesktopComponent.html";
import {IOnDestroy, IOnInit} from "angular";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {OrderDepthModels} from "../../models/OrderDepthModels";
import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";
import {OrderDepthStore} from "../../store/OrderDepthStore";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {buildQuoteRequest} from "../../../quote/util/QuoteStoreUtil";
import {CompanyInfo} from "../../../types/Company";
import {ComponentCallback} from "../../../types/AngularJS";
import {CodeAndExchange} from "../../../instruments/types/CodeAndExchange";
import {DepthService} from "../../../services/depth/DepthService";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {ThemingStore} from "../../../theming/store/ThemingStore";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {CurrentUserService} from "../../../services/user/CurrentUserService";

export const OrderDepthMobile = "tfOrderDepthMobile";

export function orderDepthMobile() {
    return {
        template: templateMobile,
        bindings: {
            security: "<",
            initialRowsDisplayed: "<",
            onSecurityChanged: "&"
        },
        controllerAs: "ctrl",
        controller: OrderDepthController
    };
}

export const OrderDepthDesktop = "tfOrderDepthDesktop";

export function orderDepthDesktop() {
    return {
        template: templateDesktop,
        bindings: {
            security: "<",
            initialRowsDisplayed: "<",
            onSecurityChanged: "&"
        },
        controllerAs: "ctrl",
        controller: OrderDepthController
    };
}

export class OrderDepthController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.orderDepthStore.updateCode(security);
            this.strategy = this.priceStrategyService.forCode(security.code);
        }
    }

    /** @input */
    public set initialRowsDisplayed(rows: number | null) {
        if (rows != null) {
            this.orderDepthStore.updateDefaultRowCount(rows);
        }
    }

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    public readonly currentCode$: Observable<CodeAndExchange>;
    public readonly hasHiddenRows$: Observable<boolean>;
    public readonly bids$: Observable<OrderDepthModels>;
    public readonly asks$: Observable<OrderDepthModels>;
    public readonly currentQuote$ = new ReplaySubject<QuoteViewModel>(1);

    public strategy: PriceStrategy;

    private readonly orderDepthStore: OrderDepthStore;
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly quoteStore: QuoteStore,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly themingStore: ThemingStore,
                       depthService: DepthService,
                       systemEventService: SystemEventService,
                       currentUserService: CurrentUserService) {
        this.orderDepthStore = new OrderDepthStore(depthService, systemEventService, currentUserService);
        this.currentCode$ = this.orderDepthStore.currentCode;
        this.bids$ = this.orderDepthStore.bids;
        this.asks$ = this.orderDepthStore.asks;
        this.hasHiddenRows$ = this.orderDepthStore
                                  .depth
                                  .map((d) => d.hasHiddenRows)
                                  .publishReplay(1)
                                  .refCount();
        this.themingStore
            .currentTheme
            .takeUntil(this.destroyed$)
            .subscribe((theme) => this.orderDepthStore.updateTheme(theme));
    }

    public $onInit(): void {
        this.watchCodeQuote();
    }

    private watchCodeQuote() {
        this.currentCode$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((security) => buildQuoteRequest(security, PortfolioType.BROKER))
            .switchMap((quoteRequest) => this.quoteStore.stream(quoteRequest))
            .subscribe((q) => this.currentQuote$.next(q));
    }

    public async selectSecurity(company?: CompanyInfo) {
        if (company != null) {
            const security = ObjectUtil.pick(company, "code", "exchange");
            this.orderDepthStore.updateCode(security);
            this.onSecurityChanged({security});
        }
    }

    public toggleBidExpansion(price: number) {
        this.orderDepthStore.toggleBid(price);
    }

    public toggleAskExpansion(price: number) {
        this.orderDepthStore.toggleAsk(price);
    }

    public showHiddenRows() {
        this.orderDepthStore.showHiddenRows();
    }

    public $onDestroy(): void {
        this.orderDepthStore.destroy();

        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
